import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-shield-line',
  'Pants/Shorts': 'ri-bar-chart-horizontal-line',
  'Headwear': 'ri-customer-service-2-line',
  'Sets': 'ri-stack-line',
  'underwear/underpants': 'ri-user-line',
  'Jersey': 'ri-trophy-line',
  'Accessories': 'ri-vip-diamond-line',
  'Bags': 'ri-shopping-bag-3-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-contrast-drop-2-line',
  'OtherStuff': 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/98 backdrop-blur-md shadow-md border-b border-background-200/80'
        : 'bg-white/90 backdrop-blur-sm border-b border-background-200/60'
    }`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer flex-shrink-0">
            <span className="text-lg font-heading font-bold text-foreground-900 tracking-tight">
              Mulebuy<span className="text-primary-500">Spreadsheets</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-white border border-background-200/80 rounded-xl py-2 max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded bg-background-100 text-primary-500">
                        <i className={`text-xs ${categoryIcons[cat.name] || 'ri-store-line'}`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/mulebuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-foreground-900 text-white text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileCatOpen(false); }}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70 pt-3">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer w-full"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform text-foreground-500 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-3 pl-3 border-l-2 border-background-200 space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-primary-500 flex-shrink-0">
                        <i className={`text-xs ${categoryIcons[cat.name] || 'ri-store-line'}`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <Link
                to="/article/mulebuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>

              <div className="pt-2 px-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-5 py-3 rounded-full bg-foreground-900 text-white text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}